<template>
    <div
        v-if="initialisedFormStructure"
        class="voc-feedback">
        <div
            :class="{
                'voc-feedback__background': !drawer,
                'voc-feedback__background--dark': !drawer && isPendoDarkMode,
            }" />
        <div
            v-show="!feedbackSubmitted"
            class="voc-feedback__form"
            :class="{
                'voc-feedback__form--dropping': dragContainerVisible,
            }"
            @dragenter.prevent="onDragEnter">
            <h2
                v-if="!drawer"
                class="voc-feedback__title">
                Submit feedback
            </h2>
            <h5
                v-if="!drawer"
                class="voc-feedback__subTitle">
                Please take time to fill in all of the required details below.
            </h5>
            <div class="voc-feedback__card-wrapper">
                <pendo-button
                    v-if="drawer"
                    v-pendo-tooltip="{
                        content: copyFormTooltip,
                        arrow: true,
                        show: copyFormTooltip,
                        trigger: 'manual',
                    }"
                    v-clipboard:copy="copyFormLink"
                    v-clipboard:success="onCopySuccess"
                    v-clipboard:error="onCopyError"
                    class="voc-feedback__button"
                    label="Copy form link"
                    block
                    :icon="copyFormIcon"
                    theme="app"
                    type="secondary"
                    @click="resetCopyMessage" />
                <pendo-card
                    class="voc-feedback__card"
                    data-cy="voc-feedback-form-card">
                    <div class="voc-feedback__cardForm">
                        <pendo-form
                            ref="form"
                            v-model="isFormValid"
                            label-position="top"
                            :model="feedbackForm">
                            <div v-if="!isSalesforce">
                                <pendo-form-item
                                    :required="true"
                                    :rules="rules.isCustomer"
                                    prop="isCustomer"
                                    label="Customer">
                                    <account-visitor-picker
                                        ref="accountVisitorPicker"
                                        class="voc-feedback__customerPicker"
                                        :init-account-id="initAccountId"
                                        :init-visitor-id="initVisitorId"
                                        @visitor="onVisitorChange"
                                        @account="onAccountChange"
                                        @is-valid="onVisitorAccountValidChange" />
                                </pendo-form-item>
                            </div>
                            <div v-if="isSalesforce">
                                <pendo-form-item
                                    ref="sfCustomer"
                                    :required="true"
                                    :rules="rules.isCustomer"
                                    prop="isCustomer"
                                    label="Customer">
                                    <div class="salesforce-details">
                                        <div
                                            v-if="loadingSalesforce"
                                            class="salesforce-account__loading">
                                            <pendo-loading-indicator size="small" />
                                        </div>
                                        <pendo-input
                                            v-if="!loadingSalesforce && singleSalesforceAccount"
                                            v-model="salesforceAccountDisplayName"
                                            placeholder="No account provided"
                                            :data-cy="`voc-feedback-item-salesforceaccount`"
                                            :disabled="true"
                                            size="small" />
                                        <account-visitor-picker
                                            v-if="!loadingSalesforce && !singleSalesforceAccount"
                                            ref="accountVisitorPicker"
                                            class="voc-feedback__customerPicker"
                                            :salesforce-account-objects="salesforceAccountObjects"
                                            :is-salesforce-account-mode="true"
                                            @account="onAccountChange"
                                            @is-valid="onVisitorAccountValidChange" />
                                        <pendo-input
                                            v-if="!loadingSalesforce && initVisitorId !== ''"
                                            v-model="salesforceVisitorDisplayName"
                                            placeholder="No visitor provided"
                                            :data-cy="`voc-feedback-item-salesforcevisitor`"
                                            :disabled="true"
                                            size="small" />
                                    </div>
                                </pendo-form-item>
                            </div>
                            <div
                                v-for="(field, index) in formData"
                                :id="field.title"
                                :key="field.title">
                                <pendo-form-item
                                    :data-cy="`voc-feedback-item-formData${index}`"
                                    :required="field.required"
                                    :rules="rules[formatFieldName(index)]"
                                    :prop="formatFieldName(index)"
                                    :label="field.title">
                                    <pendo-input
                                        v-model="feedbackForm[formatFieldName(index)]"
                                        rows="5"
                                        :data-cy="`voc-feedback-formData${index}`"
                                        :type="computeInputType(field.type)"
                                        :placeholder="field.prompt"
                                        @input="onInputChange($event, index)" />
                                </pendo-form-item>
                            </div>
                            <div>
                                <voc-item-attachments-workaround
                                    class="voc-item-attachment__wrapper"
                                    :can-delete="true"
                                    :can-add="true"
                                    :drag-and-dropped-files="dragAndDroppedFiles"
                                    @validate-error="(message) => showErrorNotification(message, true)" />
                            </div>
                            <div>
                                <pendo-form-item
                                    data-cy="voc-feedback-form-label"
                                    prop="labels"
                                    label="Labels">
                                    <voc-item-multiselect
                                        location="feedback-form"
                                        type="label"
                                        class="voc-feedback__multiselect"
                                        :options="labelOptions"
                                        :selected-items="feedbackForm.labels"
                                        @labels-changed="onLabelsChange" />
                                </pendo-form-item>
                            </div>
                            <pendo-form-item
                                v-if="isMultiApp"
                                data-cy="voc-feedback-form-app"
                                prop="apps"
                                label="Apps"
                                :rules="rules.apps">
                                <voc-item-multiselect
                                    location="feedback-form"
                                    type="app"
                                    class="voc-feedback__multiselect"
                                    :options="apps"
                                    :selected-items="feedbackForm.apps"
                                    @apps-changed="onAppsChange({ apps: $event })" />
                            </pendo-form-item>
                            <pendo-form-item
                                data-cy="voc-feedback-form-product-area"
                                prop="productAreas"
                                label="Product areas">
                                <div class="voc-feedback__product-areas">
                                    <voc-item-multiselect
                                        location="feedback-form"
                                        type="product-area"
                                        class="voc-feedback__multiselect"
                                        :options="productAreaOptions"
                                        :selected-items="feedbackForm.productAreas"
                                        @product-areas-changed="onProductAreasChange({ productAreas: $event })"
                                        @create-product-area="showGroupModal" />
                                    <div
                                        v-pendo-tooltip="{
                                            content: paClassificationTooltip,
                                            delay: { show: 300, hide: 0 },
                                            arrow: true,
                                        }" />
                                </div>
                            </pendo-form-item>
                            <pendo-form-item
                                data-cy="voc-feedback-form-rating-dropdown"
                                class="voc-feedback__rating"
                                prop="rating"
                                label="Importance"
                                :rules="rules.rating">
                                <pendo-multiselect
                                    placeholder="Select importance"
                                    :value="feedbackForm.rating"
                                    :options="ratingOptions"
                                    :full-width="true"
                                    :max-menu-width="350"
                                    @select="onRatingChange" />
                            </pendo-form-item>
                            <div class="idea-card__add">
                                <pendo-form-item
                                    prop="linkedIdeas"
                                    class="voc-feedback__linkIdeas"
                                    label="Link ideas">
                                    <voc-item-picker
                                        voc-item-type="idea"
                                        :selected-items="feedbackForm.linkedIdeas"
                                        @select-item="addItem($event, false)"
                                        @remove-item="removeItem"
                                        @open-item="openItem" />
                                </pendo-form-item>
                            </div>
                            <div
                                v-if="hasSuggestionPermissions"
                                class="idea-card__add">
                                <div
                                    v-if="displayedSuggestedIdeas.length > 0"
                                    class="voc-feedback__suggestions-title">
                                    <span class="voc-feedback__suggestions-title-text">Link suggested ideas</span>
                                    <pendo-loading-indicator
                                        v-show="loadingSuggestedIdeas"
                                        size="small" />
                                </div>
                                <transition-group
                                    name="slider"
                                    tag="div"
                                    class="voc-feedback__suggestions-cards">
                                    <voc-item-card
                                        v-for="vocItem in displayedSuggestedIdeas"
                                        :key="vocItem.id"
                                        :item="vocItem"
                                        has-border
                                        :actions="actions"
                                        @action="handleAction" />
                                </transition-group>
                            </div>
                        </pendo-form>
                        <div
                            v-if="!drawer"
                            class="voc-feedback__submit">
                            <pendo-button
                                data-cy="voc-feedback-form-submit-button"
                                theme="app"
                                type="primary"
                                label="Submit feedback"
                                :disabled="!isFormValid"
                                :loading="saving"
                                @click="submitForm" />
                        </div>
                    </div>
                </pendo-card>
            </div>
            <group-modal
                :visible="groupModalVisible"
                @close="groupModalVisible = false"
                @create-group="
                    (productArea) =>
                        onProductAreasChange({
                            productAreas: [productArea],
                            afterProductAreaCreated: true,
                        })
                " />
            <div
                v-if="dragContainerVisible"
                class="voc-feedback__drag-upload"
                :class="{ 'voc-feedback__drag-upload--dropping': dragContainerVisible }"
                @dragover.prevent
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop">
                <img
                    src="@img/voc/pendo-sparkles.svg"
                    height="100"
                    width="100">
                <h5 class="voc-feedback__help-title">
                    Drop to upload!
                </h5>
                <p class="voc-feedback__help-text">
                    Max file size: <span class="voc-feedback__help-value">10MB</span> <br>
                    File types: <span class="voc-feedback__help-value">JPG, JPEG & PNG</span><br>
                    Max files:<span class="voc-feedback__help-value"> 10 </span>
                </p>
            </div>
        </div>
        <div
            v-show="feedbackSubmitted"
            class="voc-feedback__thankyou">
            <pendo-card
                width="720px"
                class="voc-feedback__thankyouCard"
                data-cy="voc-feedback-form-thankyou-card">
                <div class="voc-feedback__thankyouMessage">
                    <h2 class="voc-feedback__title">
                        Thank you!
                    </h2>
                    <h5 class="voc-feedback__subTitle">
                        We appreciate you taking the time to share your thoughts with us.
                    </h5>
                    <div class="voc-feedback__thankyouActions">
                        <pendo-button
                            theme="app"
                            icon="plus"
                            :type="showNavBar ? 'secondary' : 'primary'"
                            label="Add another"
                            @click="goBackToForm" />
                        <pendo-button
                            v-if="showNavBar"
                            theme="app"
                            type="primary"
                            label="View feedback"
                            data-cy="voc-feedback-form-open-item"
                            @click="goToFeedback" />
                    </div>

                    <template v-if="hasIdeasWorkarounds">
                        <pendo-divider />
                        <voc-feedback-form-workarounds :ideas-workarounds="workaroundsLinkedIdeas" />
                    </template>
                </div>
            </pendo-card>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import {
    PendoButton,
    PendoCard,
    PendoDivider,
    PendoForm,
    PendoFormItem,
    PendoInput,
    PendoLoadingIndicator,
    PendoMultiselect,
    PendoNotification,
    PendoTooltip,
} from '@pendo/components';

import GroupModal from '@/groups/group-modal/group-modal.vue';
import AccountVisitorPicker from '@/voc/components/account-visitor-picker/account-visitor-picker';
import VocFeedbackFormWorkarounds from '@/voc/components/voc-feedback-form/voc-feedback-form-workarounds/voc-feedback-form-workarounds.vue';
import VocItemAttachmentsWorkaround from '@/voc/components/voc-item-attachments-workaround/voc-item-attachments-workaround.vue';
import VocItemCard from '@/voc/components/voc-item-card/voc-item-card.vue';
import VocItemMultiselect from '@/voc/components/voc-item-multiselect/voc-item-multiselect.vue';
import VocItemPicker from '@/voc/components/voc-item-picker/voc-item-picker.vue';

import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import pick from 'lodash/pick';

import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';
import { fetchSuggestionEmbedding, getPendoAccount, getPendoVisitor, loadSuggestions } from '@/voc/api/voc-items.api';
import { flattenLabels, formatLabels, formatLabelsForRequest } from '@/voc/helpers/common.helper';
import { hasSetupExplore } from '@/voc/helpers/permissions.helper';

import { FEEDBACK_IMPORTANCES as ratingOptions } from '@/voc/constants/constants';

const DEBOUNCE_TIMEOUT = 400;

export default {
    name: 'VocFeedbackForm',
    directives: {
        PendoTooltip,
    },
    components: {
        PendoLoadingIndicator,
        PendoCard,
        PendoForm,
        PendoFormItem,
        PendoInput,
        AccountVisitorPicker,
        PendoMultiselect,
        PendoButton,
        GroupModal,
        VocItemAttachmentsWorkaround,
        VocItemMultiselect,
        VocItemPicker,
        VocFeedbackFormWorkarounds,
        PendoDivider,
        VocItemCard,
    },
    props: {
        drawer: {
            type: Boolean,
            default: false,
        },
        query: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['input', 'closed'],
    setup () {
        const { isPendoDarkMode } = useDarkMode();

        return {
            isPendoDarkMode,
        };
    },
    data () {
        return {
            feedbackSubmitted: false,
            isFormValid: false,
            initialisedFormStructure: false,
            saving: false,
            groupModalVisible: false,
            feedbackForm: {
                account: null,
                visitor: null,
                isCustomer: null,
                labels: [],
                apps: [],
                productAreas: [],
                rating: null,
                linkedIdeas: [],
            },
            initAccountId: '',
            initVisitorId: '',
            rules: {
                apps: [
                    {
                        required: true,
                        message: 'At least one app required',
                    },
                ],
                isCustomer: [
                    {
                        required: true,
                        message: 'Please choose an account',
                    },
                ],
                rating: [
                    {
                        required: true,
                        message: 'Please choose an importance',
                    },
                ],
            },
            dragContainerVisible: false,
            dragAndDroppedFiles: [],
            showNavBar: false,
            createdItemId: '',
            forceValidate: false,
            source: {},
            isSalesforce: false,
            loadingSalesforce: false,
            salesforceAccountDisplayName: '',
            singleSalesforceAccount: false,
            salesforceVisitorDisplayName: '',
            workaroundsLinkedIdeas: [],
            debounceId: 0,
            suggestedIdeas: [],
            acceptedSuggestions: [],
            rejectedSuggestions: [],
            ratingOptions,
            copyFormTooltip: '',
            copyFormIcon: 'link',
            salesforceAccountObjects: [],
            actions: [
                {
                    id: 'view',
                    icon: 'external-link',
                    label: this.$t('common.View'),
                },
                {
                    id: 'accept',
                    icon: 'check',
                    label: this.$t('common.Accept'),
                },
                {
                    id: 'reject',
                    icon: 'x',
                    label: this.$t('common.Reject'),
                },
            ],
        };
    },
    computed: {
        ...mapState({
            localAttachmentList: (state) => state.vocItems.attachmentList,
            loadingSuggestedIdeas: (state) => state.vocItems.loadingSuggestedIdeas,
        }),
        ...mapGetters({
            apps: 'vocItems/listenAppOptions',
            isMultiApp: 'apps/isMultiApp',
            initialFormData: 'vocItems/formData',
            statuses: 'statuses/list',
            productAreas: 'groups/list',
            hasSuggestionPermissions: 'vocItems/hasSuggestionPermissions',
            canSubmitFeedback: 'vocItems/canSubmitFeedback',
            labels: 'voc/labels/feedbackLabels',
        }),
        formData () {
            return cloneDeep(this.initialFormData('feedback'));
        },
        hasIdeasWorkarounds () {
            return this.workaroundsLinkedIdeas.length > 0;
        },
        isExploreView () {
            return hasSetupExplore();
        },
        feedbackFormStatus () {
            return {
                isFormValid: this.isFormValid,
                saving: this.saving,
                feedbackSubmitted: this.feedbackSubmitted,
            };
        },
        enableProductAreaClassification () {
            return this.feedbackForm[this.formatFieldName(1)].trim().length > 5;
        },
        paClassificationTooltip () {
            if (this.enableProductAreaClassification) {
                return 'AI assisted: Categorise feedback item into a product area';
            }

            return 'AI assisted: Description too short to categorise feedback item';
        },
        displayedSuggestedIdeas () {
            return this.suggestedIdeas.filter((idea) => {
                const allIdeasToBeRemoved = this.feedbackForm.linkedIdeas.concat(this.rejectedSuggestions);

                return !allIdeasToBeRemoved.some((item) => item.id === idea.id);
            });
        },
        copyFormLink () {
            return `${window.location.origin}${this.$router.pendo.toPath({
                name: 'ValidateFeedbackSavedViews',
                query: { context: 'createFeedbackForm' },
            })}`;
        },
        labelOptions () {
            return formatLabels(this.labels);
        },
        productAreaOptions () {
            return this.productAreas.map((productArea) => pick(productArea, ['id', 'name', 'color']));
        },
    },
    watch: {
        feedbackFormStatus: {
            handler () {
                this.$emit('input', this.feedbackFormStatus);
            },
            deep: true,
            immediate: true,
        },
    },
    async created () {
        this.showNavBar = this.$route?.name !== 'ValidateFeedbackFormExternal';
        if (this.canSubmitFeedback) {
            await Promise.all([
                this.loadLabels(),
                this.loadGroups(),
                this.loadStatuses({ type: 'feedback' }),
                this.loadInitialFormData({ type: 'feedback' }),
                this.loadSetting({ setting: 'accountDisplayName', retrieveMetadata: false }).catch((error) => error),
            ]);
            this.formData.forEach((_field, index) => {
                this.feedbackForm[`${this.formatFieldName(index)}`] = '';
                if (index === 0) {
                    this.rules[`${this.formatFieldName(index)}`] = [
                        {
                            required: true,
                            message: 'This field is required',
                        },
                        {
                            whitespace: true,
                            message: 'This field cannot be whitespace only. Please enter valid characters.',
                        },
                        {
                            max: 255,
                            message: 'This field should be less than 255 characters',
                        },
                    ];
                }
                if (index === 1) {
                    this.rules[`${this.formatFieldName(index)}`] = [
                        {
                            whitespace: true,
                            message: 'This field cannot be whitespace only. Please enter valid characters.',
                        },
                        {
                            max: 5000,
                            message: 'This field should be less than 5000 characters',
                        },
                    ];
                }
            });

            if (this.query?.vocItem) {
                this.loadFromQuery();
            } else {
                this.loadFromParams();
            }
            this.initialisedFormStructure = true;
        } else {
            this.$router.replace('/404');
        }
    },
    mounted () {
        this.resetAttachmentList();
        if (!this.showNavBar && this.canSubmitFeedback) {
            const navBar = document.querySelector('.primary-nav-bar');
            const topNav = document.querySelector('.pendo-top-nav');
            if (navBar) navBar.style.display = 'none';
            if (topNav) topNav.style.display = 'none';

            for (const main of document.getElementsByTagName('main')) {
                main.classList.add('voc-feedback__fullscreen');
            }
        }
        this.loadSavedViews({ type: 'feedback' });
    },
    unmounted () {
        if (!this.showNavBar && this.canSubmitFeedback) {
            const navBar = document.querySelector('.primary-nav-bar');
            const topNav = document.querySelector('.pendo-top-nav');
            if (navBar) navBar.style.display = 'initial';
            if (topNav) topNav.style.display = 'initial';

            for (const main of document.getElementsByTagName('main')) {
                main.classList.remove('voc-feedback__fullscreen');
            }
        }
    },
    methods: {
        ...mapActions({
            createItem: 'vocItems/createVocItem',
            loadInitialFormData: 'vocItems/loadInitialFormData',
            loadStatuses: 'statuses/loadAll',
            createAttachmentMedia: 'vocItems/createAttachmentMedia',
            resetAttachmentList: 'vocItems/resetAttachmentList',
            attachLinksToVocItem: 'vocItems/attachLinksToVocItem',
            loadLabels: 'voc/labels/loadLabels',
            loadGroups: 'groups/loadAll',
            loadSavedViews: 'savedViews/loadAll',
            setLoadingSuggestions: 'vocItems/setLoadingSuggestions',
            updateCurrentSavedView: 'savedViews/updateCurrentSavedView',
            loadSetting: 'subscription/loadSetting',
        }),
        onVisitorChange (visitor) {
            this.feedbackForm.visitor = visitor;
        },
        onAccountChange (account) {
            this.feedbackForm.account = account;
        },
        onLabelsChange (labels) {
            this.feedbackForm.labels = labels;
        },
        onAppsChange ({ apps }) {
            this.feedbackForm.apps = apps;
        },
        onInputChange (value, fieldIndex) {
            this.formData[fieldIndex].content = value;
            if (this.hasSuggestionPermissions) {
                this.searchForSuggestions();
            }
        },
        onRatingChange (option) {
            this.feedbackForm.rating = option;

            if (this.forceValidate) {
                this.$refs.form.validate();
            }
        },
        onProductAreasChange ({ productAreas, afterProductAreaCreated = false }) {
            let newProductAreas;

            if (afterProductAreaCreated) {
                newProductAreas = [...this.feedbackForm.productAreas, ...productAreas];
            } else {
                newProductAreas = productAreas;
            }
            this.feedbackForm.productAreas = newProductAreas;
        },
        async onConfirm () {
            this.saving = true;
            let createdItem = {};
            try {
                this.formData.forEach((field) => {
                    field.content = field.content.trim();
                });
                const newVocItem = {
                    status: this.statuses('feedback')[0]?.id,
                    formData: this.formData,
                    appIds: this.isMultiApp ? this.feedbackForm.apps.map((app) => app.id) : [this.apps[0].id],
                    productAreaIds: this.feedbackForm.productAreas.map((productArea) => productArea.id),
                    isPublic: true,
                    onBehalfOfType: 'Visitor',
                    onBehalfOfId: this.feedbackForm.visitor?.id,
                    accountId: this.feedbackForm.account?.id,
                    type: 'feedback',
                    labels: formatLabelsForRequest(this.feedbackForm.labels),
                    associatedItemIds: this.feedbackForm.linkedIdeas.map((linkedIdea) => linkedIdea.id),
                    source: this.source,
                    strength: this.feedbackForm.rating?.id,
                };
                createdItem = await this.createItem({
                    vocItem: newVocItem,
                    acceptedSuggestions: this.acceptedSuggestions,
                    isExploreView: this.isExploreView,
                });
                this.createdItemId = createdItem.id;

                const globalCreate = this.$route?.query?.globalCreate;
                window.pendo?.track('feedback-created', {
                    ...(globalCreate && { location: 'globalCreate' }),
                });
            } catch {
                this.showErrorNotification('The form could not be submitted at this time.');

                return;
            }

            try {
                const imageFiles = [];
                const linkFiles = [];
                let failedUploadedFiles = [];
                if (this.localAttachmentList.length > 0) {
                    this.localAttachmentList.forEach((attachment) => {
                        if (attachment.type === 'image') {
                            imageFiles.push(attachment.file);
                        } else if (attachment.type === 'link') {
                            linkFiles.push(attachment.file);
                        }
                    });
                    failedUploadedFiles = await this.createAttachmentMedia({
                        imageFiles,
                        vocItemId: createdItem.id,
                    });
                    await this.attachLinksToVocItem({
                        linkFiles,
                        vocItemId: createdItem.id,
                    });
                }
                if (failedUploadedFiles.length > 0) {
                    failedUploadedFiles.forEach((failedFile) => {
                        this.showErrorNotification(
                            `There was an error attaching ${failedFile.name} to the feedback. Try this action again later`,
                        );
                    });
                }
            } catch (err) {

                console.error(err);
                this.showErrorNotification(
                    'There was an error attaching the file to the feedback. Try this action again later',
                );
            }
            this.dragAndDroppedFiles = [];
            this.feedbackSubmitted = true;
            this.filterIdeasWorkarounds();
            this.saving = false;
        },
        async submitForm () {
            if (this.isFormValid) {
                await this.onConfirm();
            }
        },
        goBackToForm () {
            if (!this.isSalesforce) {
                this.initAccountId = '';
                this.initVisitorId = '';
                this.feedbackForm.account = null;
                this.feedbackForm.visitor = null;
                this.feedbackForm.isCustomer = null;
            }

            this.$refs.accountVisitorPicker?.clearAccount();
            this.$refs.accountVisitorPicker?.clearVisitor();
            this.createdItemId = '';
            this.feedbackForm.labels = [];
            this.feedbackForm.productAreas = [];
            this.feedbackForm.rating = null;
            this.feedbackForm.linkedIdeas = [];
            this.suggestedIdeas = [];
            this.acceptedSuggestions = [];
            this.rejectedSuggestions = [];
            if (this.isMultiApp) {
                this.feedbackForm.apps = [];
            }
            this.formData.forEach((field, index) => {
                field.content = '';
                this.feedbackForm[this.formatFieldName(index)] = '';
            });
            this.resetAttachmentList();
            this.$refs.form.reset();

            // If this method has been called and the page was loaded from salesforce, then the isCustomer must have been 'yes'.
            // Resetting it to yes skips some account lookup verification
            if (this.isSalesforce && this.singleSalesforceAccount) {
                this.feedbackForm.isCustomer = 'yes';
            }
            this.feedbackSubmitted = false;
        },
        showErrorNotification (msg, html = false) {
            PendoNotification({
                type: 'error',
                title: 'Something went wrong',
                message: msg,
                duration: 5000,
                dangerouslyUseHTMLString: html,
            });
        },
        computeInputType (fieldType) {
            if (fieldType === 'long text') {
                return 'textarea';
            }

            return 'text';
        },
        formatFieldName (index) {
            const fieldName = `formData${index}`;

            return fieldName;
        },
        onVisitorAccountValidChange (isValid) {
            this.feedbackForm.isCustomer = isValid ? 'yes' : '';
            this.$refs.form.validateField(['isCustomer']);
        },
        onDragEnter () {
            this.dragContainerVisible = true;
        },
        onDragLeave () {
            this.dragContainerVisible = false;
            this.dragAndDroppedFiles = [];
        },
        onDrop (evt) {
            const fileList = [];
            if (evt.dataTransfer.items) {
                [...evt.dataTransfer.items].forEach((item) => {
                    if (item.kind === 'file') {
                        const file = item.getAsFile();
                        fileList.push(file);
                    }
                });
                this.dragAndDroppedFiles = fileList;
            }
            this.dragContainerVisible = false;
        },
        goToFeedback () {
            this.updateCurrentSavedView({ property: 'applied', value: false, persist: false });
            this.$emit('closed', this.createdItemId);
        },
        async loadFromParams () {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);

            const incomingAccountId = urlParams.get('acc');
            if (incomingAccountId) {
                this.initAccountId = incomingAccountId;
                this.forceValidate = true;
            }
            const incomingVisitorId = urlParams.get('vis');
            if (incomingVisitorId) {
                this.initVisitorId = incomingVisitorId;
                this.forceValidate = true;
            }

            const incomingSource = urlParams.get('source');
            if (incomingSource && incomingSource.toLowerCase() === 'salesforce') {
                this.source = {
                    sourceCategory: 'Integrations',
                    source: 'Salesforce',
                };
                this.isSalesforce = true;
                this.loadingSalesforce = true;

                if (incomingAccountId) {
                    await this.processSalesforceAccountResponse(incomingAccountId);
                }

                if (incomingVisitorId) {
                    try {
                        const vis = await getPendoVisitor(incomingVisitorId);
                        this.feedbackForm.visitor = vis[0];
                        this.salesforceVisitorDisplayName = get(this.feedbackForm.visitor, 'id', '');
                        const email = get(this.feedbackForm.visitor, 'metadata.agent.email', '');
                        if (email !== '') {
                            this.salesforceVisitorDisplayName = `${this.salesforceVisitorDisplayName} | ${email}`;
                        }
                    } catch {
                        PendoNotification({
                            type: 'error',
                            duration: 3000,
                            title: 'Salesforce',
                            message: 'The Salesforce contact could not be found in Pendo',
                        });
                        this.rules.isCustomer[0].message = 'Unable to match the Salesforce record to a Pendo visitor';
                    }
                }
                this.loadingSalesforce = false;

                if (this.feedbackForm.account && (this.feedbackForm.visitor || this.initVisitorId === '')) {
                    this.feedbackForm.isCustomer = 'yes';
                } else if (this.salesforceAccountObjects.length === 0) {
                    this.feedbackForm.isCustomer = '';
                    this.$refs.sfCustomer.touch();
                    this.$refs.form.validateField(['isCustomer']);
                }
            }

            const incomingTitle = urlParams.get('title');
            if (incomingTitle) {
                this.feedbackForm[this.formatFieldName(0)] = incomingTitle;
                this.formData[0].content = incomingTitle;
            }

            const incomingDescription = urlParams.get('desc');
            if (incomingDescription) {
                this.feedbackForm[this.formatFieldName(1)] = incomingDescription;
                this.formData[1].content = incomingDescription;
            }

            const incomingProductAreaId = urlParams.get('paId');
            if (
                incomingProductAreaId &&
                this.productAreas.find((productArea) => productArea.id === incomingProductAreaId)
            ) {
                this.feedbackForm.productAreas = [
                    this.productAreas.find((productArea) => productArea.id === incomingProductAreaId),
                ];
            }

            const incomingAppId = parseInt(urlParams.get('appId'));
            if (incomingAppId && this.isMultiApp && this.apps.find((app) => app.id === incomingAppId)) {
                this.feedbackForm.apps = [this.apps.find((app) => app.id === incomingAppId)];
            }
        },
        async processSalesforceAccountResponse (incomingAccountId) {
            try {
                const accounts = await getPendoAccount(incomingAccountId);
                if (accounts.length === 0) {
                    this.singleSalesforceAccount = true;
                    PendoNotification({
                        type: 'error',
                        duration: 3000,
                        title: 'Salesforce',
                        message: 'The Salesforce account could not be found in Pendo',
                    });
                    this.rules.isCustomer[0].message = 'Unable to match the Salesforce record to a Pendo account';
                } else if (accounts.length === 1) {
                    this.singleSalesforceAccount = true;
                    this.feedbackForm.account = accounts[0];
                    this.salesforceAccountDisplayName = this.getDisplayNameFromAccount(this.feedbackForm.account);
                } else {
                    this.salesforceAccountObjects = accounts;
                }
            } catch {
                this.singleSalesforceAccount = true;
                PendoNotification({
                    type: 'error',
                    duration: 3000,
                    title: 'Salesforce',
                    message: 'The Salesforce account could not be found in Pendo',
                });
                this.rules.isCustomer[0].message = 'Unable to match the Salesforce record to a Pendo account';
            }
        },
        getDisplayNameFromAccount (account) {
            const displayName = get(account, `metadata.${this.accountDisplayName}`, '');
            const sfdcName = get(account, 'metadata.salesforce.name', '');

            if (displayName !== '') {
                if (displayName !== sfdcName && sfdcName !== '') {
                    return `${displayName} | ${sfdcName}`;
                }

                if (displayName !== account.id) {
                    return `${displayName} | ${account.id}`;
                }

                return displayName;
            }

            if (sfdcName !== '') {
                return sfdcName;
            }

            return account.id;
        },
        loadFromQuery () {
            this.feedbackForm.linkedIdeas.push(this.query.vocItem);

            const incomingLabelIds = this.query?.vocItem?.labelIds;
            if (incomingLabelIds) {
                const filteredLabels = this.labels.filter((label) => this.query.vocItem.labelIds.includes(label.id));
                this.feedbackForm.labels = flattenLabels(formatLabels(filteredLabels));
            }

            const incomingAppIds = this.query?.vocItem?.appIds;
            if (incomingAppIds && this.isMultiApp) {
                this.query.vocItem.appIds.forEach((idToFind) => {
                    this.feedbackForm.apps.push(this.apps.find((app) => app.id === idToFind));
                });
            }

            const incomingProductAreaIds = this.query?.vocItem?.productAreaIds;
            if (incomingProductAreaIds) {
                this.query.vocItem.productAreaIds.forEach((idToFind) => {
                    this.feedbackForm.productAreas.push(
                        this.productAreas.find((productArea) => productArea.id === idToFind),
                    );
                });
            }
        },
        addItem (item, isSuggestion = false) {
            this.feedbackForm.linkedIdeas.push(item);
            if (isSuggestion) {
                this.acceptedSuggestions.push(item.id);
            }
        },
        removeItem (vocItem) {
            this.feedbackForm.linkedIdeas = this.feedbackForm.linkedIdeas.filter((item) => item.id !== vocItem.id);
            this.acceptedSuggestions = this.acceptedSuggestions.filter((id) => id !== vocItem.id);
        },
        filterIdeasWorkarounds () {
            this.workaroundsLinkedIdeas = this.feedbackForm.linkedIdeas.filter((item) => {
                return item.workaround && item.workaround.body !== '';
            });
        },
        async searchForSuggestions () {
            this.setLoadingSuggestions({ loading: true });
            if (this.debounceId) clearTimeout(this.debounceId);
            this.debounceId = setTimeout(async () => {
                await this.setSuggestions();
            }, DEBOUNCE_TIMEOUT);
        },
        async setSuggestions () {
            try {
                if (this.feedbackForm[this.formatFieldName(0)] || this.feedbackForm[this.formatFieldName(1)]) {
                    const embeddingData = await fetchSuggestionEmbedding({
                        title: this.feedbackForm[this.formatFieldName(0)],
                        description: this.feedbackForm[this.formatFieldName(1)],
                    });
                    const embedding = embeddingData?.predictions[0].embeddings.values;
                    const suggestions = await loadSuggestions({ embedding, type: 'idea' });

                    this.suggestedIdeas = suggestions.slice(0, 2).map((suggestion) => {
                        return {
                            ...suggestion,
                            vocItemTitle: suggestion.formData[0].content,
                        };
                    });
                    this.setLoadingSuggestions({ loading: false });
                } else {
                    this.suggestedIdeas = [];
                    this.setLoadingSuggestions({ loading: false });
                }
            } catch {
                this.setLoadingSuggestions({ loading: false });
            }
        },
        addSuggestionToRemove (vocItem) {
            this.rejectedSuggestions.push(vocItem);
        },
        openItem (vocItem) {
            window.open(
                this.$router.resolve({
                    name: 'Validate',
                    query: { openItem: vocItem.id },
                }).href,
            );
        },
        onCopySuccess () {
            this.copyFormTooltip = 'Copied!';
            this.copyFormIcon = 'check';
        },
        onCopyError () {
            this.copyFormTooltip = 'Failed to copy';
            this.copyFormIcon = 'x';
        },
        resetCopyMessage () {
            setTimeout(() => {
                this.copyFormTooltip = '';
                this.copyFormIcon = 'link';
            }, 1000);
        },
        showGroupModal (toggleMenu) {
            toggleMenu();

            this.groupModalVisible = true;
        },
        handleAction ({ action, item }) {
            switch (action.id) {
                case 'view':
                    this.openItem(item);
                    break;
                case 'accept':
                    this.addItem(item, true);
                    break;
                case 'reject':
                    this.addSuggestionToRemove(item);
                    break;
            }
        },
    },
};
</script>

<style scoped lang="less">
.pendo-form {
    display: inherit;
}

.voc-feedback {
    width: 100%;
    display: flex;
    height: 100%;

    &__background {
        background: url('~@img/voc/voc-feedback-form-background.svg') no-repeat center top 40px;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: -1;
    }

    &__background--dark {
        filter: invert(1) sepia(1) hue-rotate(190deg) saturate(0.4);
    }

    &__button {
        margin-left: auto;
        margin-bottom: 6px;
        width: 150px;
    }

    &__suggestions-cards {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 8px;
    }

    &__form {
        position: relative;
        padding: 72px 0;
        width: 100%;
        overflow: auto;

        &--dropping {
            padding-top: 0;
            pointer-events: none;

            * {
                pointer-events: none;
            }
        }

        .voc-feedback__title {
            font-weight: 600;
            position: relative;
            margin: 20px auto 4px;
            width: 720px;
        }

        .voc-feedback__subTitle {
            font-weight: 400;
            position: relative;
            margin: 10px auto 24px;
            width: 720px;
        }
    }

    &__thankyou {
        margin: 200px auto;

        .voc-feedback__title {
            font-weight: 600;
            position: relative;
            margin-bottom: 4px;
        }

        .voc-feedback__subTitle {
            font-weight: 400;
            position: relative;
            margin-bottom: 24px;
        }
    }

    &__thankyouCard {
        overflow: hidden;
    }

    &__cardForm {
        padding: 16px;
    }

    &__customerPicker {
        display: flex;
        gap: 16px;
        max-width: 656px;
        width: 100%;

        :deep(.account-picker) {
            flex: 0.5;
            max-width: 50%;
            margin-bottom: 0;
        }

        :deep(.visitor-picker) {
            flex: 0.5;
            max-width: 50%;
            margin-bottom: 0;
        }
    }

    &__submit {
        display: flex;
        flex-direction: row-reverse;
        margin-top: 10px;
    }

    &__thankyouMessage {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__card-wrapper {
        width: 720px;
        margin: 0 auto;
    }

    &__product-areas {
        display: flex;
        align-items: center;
    }

    &__groupSelectorWand {
        color: var(--palette-purple-80);
        border-color: transparent;
        background-color: transparent;
        margin-left: 4px;

        &:hover {
            color: var(--palette-purple-80);
            border-color: transparent;
        }
    }

    &__groupSelectorWandLoading {
        height: 36px;
        width: 36px;
        margin-left: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__rating {
        grid-column-start: 1;
        grid-column-end: 3;
    }

    &__help-value {
        color: var(--palette-gray-70);
    }

    &__help-text {
        text-align: center;
        font-size: 14px;
    }

    &__linkIdeas {
        width: 100%;
    }

    &__suggestions-title {
        display: flex;
        gap: 4px;
        align-items: center;
        font-weight: 600;
    }

    &__suggestions-title-text {
        padding: 1px 0;
    }

    .salesforce-account__loading {
        justify-content: center;
    }

    .salesforce-details {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        gap: 16px;

        :deep(.account-picker) {
            flex: 1;
            max-width: 100%;
        }
    }

    &__drag-upload {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        pointer-events: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: auto;
        justify-content: center;
        z-index: 1;
        background-color: var(--palette-gray-20);

        &--dropping {
            pointer-events: all;
        }
    }

    &__thankyouActions {
        display: flex;
    }

    .voc-item-attachment__wrapper {
        max-width: 656px;
        margin-bottom: 16px;
    }

    :deep(&__multiselect) {
        flex: 1;

        .pendo-multiselect__trigger {
            transition: border-color 0.2s;
            border: 1px solid var(--palette-gray-40);

            &:hover {
                border-color: var(--palette-gray-100);
            }

            &.is-disabled {
                border-color: var(--palette-gray-40);
            }
        }
    }
}

@media (max-width: 720px) {
    .voc-feedback {
        &__card {
            width: 475px;
        }

        &__customerPicker {
            flex-direction: column;

            :deep(.account-picker),
            :deep(.visitor-picker) {
                max-width: 100%;
            }
        }

        .voc-item-attachment__wrapper {
            max-width: 409px;
        }

        &__form {
            .voc-feedback__title {
                width: 475px;
            }

            .voc-feedback__subTitle {
                width: 475px;
            }
        }
    }
}
</style>

<!-- eslint-disable vue-scoped-css/enforce-style-type -->
<style lang="less">
.voc-feedback__hide-nav-bar {
    z-index: 2000;
}

.voc-feedback__fullscreen {
    left: 0 !important;
    top: 0 !important;
    height: 100% !important;
}

.slider-enter-active {
    overflow: hidden;
    transition: all 0.3s ease-out;
}

.slider-leave-active {
    overflow: hidden;
    transition: all 0.3s ease-out;
}

.slider-leave-to {
    transform: translateX(50px);
    opacity: 0;
}

.slider-enter {
    transform: translateX(50px);
    opacity: 0;
}
</style>
